import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { ALL_STATES, DATE_REQUIRED_STATES, LEAD_SOURCES, LEAD_TYPES } from "../lib/states";
import { useRequirements } from "../lib/requirements";
import { X, Loader2, UserPlus } from "lucide-react";

export default function AddLeadModal({ callers = [], interactionAssignees = [], onClose, onCreated }) {
  const requirements = useRequirements();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    source: "Referral",
    lead_type: "",
    requirement: "",
    city: "Bangalore",
    first_message: "",
    state: "Fresh",
    assigned_to: "",                  // backend (calling/chat) assignee
    interaction_assignee: "",         // showroom / home-visit / video-call owner
    next_action_date: "",
    note: "",
    potential_order_value: "",        // ₹ amount (string in form, parsed on submit)
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const dateRequired = DATE_REQUIRED_STATES.includes(form.state);

  const submit = async (e) => {
    e.preventDefault();
    if (dateRequired && !form.next_action_date) {
      toast.error(`A next-action date is required for "${form.state}".`);
      return;
    }
    setBusy(true);
    try {
      const pov = (form.potential_order_value ?? "").toString().trim();
      const payload = {
        ...form,
        assigned_to: form.assigned_to || null,
        interaction_assignee: form.interaction_assignee || null,
        next_action_date: dateRequired ? form.next_action_date : null,
        note: form.note || null,
        potential_order_value: pov ? Number(pov) : null,
      };
      await api.post("/leads", payload);
      toast.success(`Lead "${form.name}" added`);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 animate-fade-in" onClick={onClose} />
      <form
        onSubmit={submit}
        data-testid="add-lead-form"
        className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-lg p-6 animate-fade-in max-h-[90vh] overflow-y-auto col-scroll"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-stone-600" />
            <h2 className="font-heading text-xl tracking-tight text-stone-900">Add lead manually</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-stone-100 text-stone-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *">
              <input required value={form.name} onChange={(e) => set("name", e.target.value)}
                data-testid="add-lead-name" className={inputCls} placeholder="Customer name" />
            </Field>
            <Field label="Phone *">
              <input required value={form.phone} onChange={(e) => set("phone", e.target.value)}
                data-testid="add-lead-phone" className={inputCls} placeholder="9845012345" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <input value={form.city} onChange={(e) => set("city", e.target.value)}
                data-testid="add-lead-city" className={inputCls} />
            </Field>
            <Field label="Source">
              <select value={form.source} onChange={(e) => set("source", e.target.value)}
                data-testid="add-lead-source" className={inputCls}>
                {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Lead type">
              <select value={form.lead_type} onChange={(e) => set("lead_type", e.target.value)}
                data-testid="add-lead-type" className={inputCls}>
                <option value="">— Select type —</option>
                {LEAD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Requirement">
              <select value={form.requirement} onChange={(e) => set("requirement", e.target.value)}
                data-testid="add-lead-requirement" className={inputCls}>
                <option value="">— Select requirement —</option>
                {requirements.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field label="First message / enquiry">
            <textarea rows={2} value={form.first_message} onChange={(e) => set("first_message", e.target.value)}
              data-testid="add-lead-message" className={`${inputCls} resize-none`}
              placeholder="What did the customer ask for? (used to auto-classify segment)" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Stage">
              <select value={form.state} onChange={(e) => set("state", e.target.value)}
                data-testid="add-lead-state" className={inputCls}>
                {ALL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Potential order value (₹)">
              <input type="number" min="0" step="1000" value={form.potential_order_value}
                onChange={(e) => set("potential_order_value", e.target.value)}
                data-testid="add-lead-potential-value" className={inputCls}
                placeholder="e.g. 300000" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Backend assignee">
              <select value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)}
                data-testid="add-lead-assignee" className={inputCls}>
                <option value="">Unassigned</option>
                {callers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Interaction assignee">
              <select value={form.interaction_assignee} onChange={(e) => set("interaction_assignee", e.target.value)}
                data-testid="add-lead-interaction-assignee" className={inputCls}>
                <option value="">Unassigned</option>
                {interactionAssignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>

          {dateRequired && (
            <Field label="Next action date *">
              <input type="date" value={form.next_action_date} onChange={(e) => set("next_action_date", e.target.value)}
                data-testid="add-lead-date" className={inputCls} />
            </Field>
          )}

          <Field label="Note">
            <textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)}
              data-testid="add-lead-note" className={`${inputCls} resize-none`} placeholder="Optional note" />
          </Field>
        </div>

        <button type="submit" disabled={busy} data-testid="add-lead-submit"
          className="mt-5 w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add lead"}
        </button>
      </form>
    </div>
  );
}

const inputCls = "mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">{label}</label>
      {children}
    </div>
  );
}
